// The six writes behind 選品: 加入清單 / 不要這款 / 換下 / 留著 / 移除 / 加回, the 全部照建議 batch,
// the 加入商品 picker's search, and the one-tap seed from the rail.
//
// Same shape as useVeto: ask, mark the row busy, then let the refetch decide. Nothing is stored in
// the browser — the list is server state shared between Magnus and Adam, and the plan that comes
// back owns it. On failure the snackbar carries the API's own zh-TW sentence.
//
// 全部照建議 is ONE request (`/list/decide-batch`), not one per proposal: it is one transaction on
// the server, so a half-applied queue is not a state the screen can end up in.
import { useCallback, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import api from '../../../services/api';
import { newReference, readNokeError } from './format';
import {
  ADD_ALREADY_NOTE, ADD_DELISTED_FALLBACK, ADD_HITS_NOTE, ADD_MISSING_FALLBACK, ADD_REJECTED_NOTE,
  EMPTY_LIST_PATCH, ListPatch, addedNote, decideBatchBody, decideBody, dropListPatch, exactSearchHit,
  httpStatusOf, listSeedBody, listSkuBody, mergeListPatch, searchRows,
  MAIN_BATCH_KEY, MAIN_BATCH_URL, MainBatchPick, mainBatchBody,
} from './counterList';
import {
  CounterListDecision, RestockV2ListProposal, RestockV2ListResponse, RestockV2ListSearchRow, TierKey,
} from './types';

const DECIDE_URL = '/api/noke/restock-v2/list/decide';
const BATCH_URL = '/api/noke/restock-v2/list/decide-batch';
const ADD_URL = '/api/noke/restock-v2/list/add';
const REMOVE_URL = '/api/noke/restock-v2/list/remove';
const SEED_URL = '/api/noke/restock-v2/list/seed';
const SEARCH_URL = '/api/noke/restock-v2/list/search';
// 不要這個顏色／尺寸 / 加回 do NOT live here: they are veto-shaped, so they run through useVeto with
// the other veto verbs and share its optimistic store. One store per kind of optimism.

// Reserved pending keys for the two controls that are not about one SKU.
export const BATCH_KEY = '__batch';
export const SEED_KEY = '__seed';
// 加入商品 while the typed SKU is still being resolved. Its own key, not the SKU's: at the moment
// he presses Enter we do not yet know which SKU (or whether any) the box names.
export const ADD_KEY = '__add';

/**
 * What pressing Enter in 加入商品 actually turned out to be.
 *
 * Magnus 2026-09-18: 「at the 選品 tab make it allow me to enter sku, to 加入清單 manually」. The
 * honest answers are not one — a SKU can already be on the list, can be one he threw off it, can be
 * 下架, can be a typo, or can simply not be a SKU at all — and each of those needs a different next
 * move from him. Collapsing them into 「找不到」 is what makes a screen feel broken: four of the five
 * are not errors.
 */
export type ManualAddKind =
  // it is on the list now — the one outcome that wrote anything
  'added'
  // already approved. Say so; the row is NOT written again (it would be a no-op UPSERT anyway)
  | 'already'
  // he pressed 不要這款 on it before. Offer 加回, never a silent re-add
  | 'rejected'
  // the style exists in the catalogue but is 下架 and has nothing at the counter (server 409)
  | 'delisted'
  // no such SKU anywhere (server 404)
  | 'missing'
  // not an exact SKU — the name-search path. `results` carries the hits
  | 'hits'
  // the write was refused for some other reason; `message` is the server's own sentence
  | 'error';

export interface ManualAdd {
  kind: ManualAddKind;
  // what he typed, so the message can quote it back
  query: string;
  sku: string;
  // the picker row behind the outcome, when there is one: title + thumbnail for the confirmation.
  row: RestockV2ListSearchRow | null;
  title: string;
  message: string;
}

export interface CounterList {
  // Keyed by SKU (plus BATCH_KEY / SEED_KEY), so a card disables only its own buttons.
  pending: Record<string, boolean>;
  // What he has answered but the engine has not recomputed yet. Rendered through applyListPatch.
  patch: ListPatch;
  // Called by the tab when a recompute LANDS, with the patch as it was when that recompute was
  // REQUESTED. A plan can only retire the answers it could have been computed from; anything
  // answered while it was in flight stays (Magnus 2026-09-17: 「why it reverted back」).
  retire: (landed: ListPatch) => void;
  // Resolves TRUE when the server took it. The 看更多 pool hides its row the instant it is tapped
  // and needs the answer to put it back on a refusal — it holds its own decided set, so it cannot
  // learn that from the shared patch being rolled back.
  decide: (sku: string, decision: CounterListDecision, tier?: TierKey | null, reason?: string | null) => Promise<boolean>;
  decideAll: (rows: RestockV2ListProposal[]) => Promise<void>;
  setMainsFromSuggestion: (picks: MainBatchPick[]) => Promise<void>;
  add: (sku: string) => Promise<void>;
  /**
   * 加入商品's ONE entry point: he types, he presses Enter, and this decides what that meant.
   * Resolves to null only when a newer query overtook this one. It adds WITHOUT a second tap when
   * the box holds an exact, undecided SKU — that is the whole point — and refuses to write in the
   * two cases where writing would be a lie about what he asked for (already approved, previously
   * rejected). Never awaited by anything that has to paint: the add itself is optimistic.
   */
  addBySku: (query: string) => Promise<ManualAdd | null>;
  remove: (sku: string) => Promise<void>;
  seed: () => Promise<void>;
  search: (query: string) => Promise<void>;
  clearSearch: () => void;
  results: RestockV2ListSearchRow[];
  searched: boolean;
  searching: boolean;
  searchError: string;
}

export function useCounterList(refetch: (immediate?: boolean) => void): CounterList {
  const { enqueueSnackbar } = useSnackbar();
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [patch, setPatch] = useState<ListPatch>(EMPTY_LIST_PATCH);
  const [results, setResults] = useState<RestockV2ListSearchRow[]>([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  // Only the newest search may write results; a slow one landing late must not overwrite it.
  const searchId = useRef(0);

  const mark = useCallback((key: string, on: boolean) => {
    setPending((old) => { const next = { ...old }; if (on) next[key] = true; else delete next[key]; return next; });
  }, []);

  /**
   * One shape for every write: mark the row moved, POST, then ASK for a refresh — never wait for
   * one. Magnus 2026-09-17: 「if i remove one, don't make it refresh everything」. /restock-v2 costs
   * ~6.3s on prod and his first curation pass is ~69 decisions, so an awaited refetch per tap was
   * about seven minutes of spinner. The tap settles locally; `refetch` coalesces the recompute.
   *
   * A refusal rolls the optimistic mark back and says why, so the screen never keeps a claim the
   * server rejected. `immediate` is for the one write too big to model locally (the rail seed).
   */
  // `data` on success, `status` + `message` on a refusal: 加入商品 has to tell 已下架 (409) from
  // 找不到 (404), and the snackbar sentence alone cannot carry that. Every other caller ignores
  // everything but `ok`.
  const write = useCallback(async (
    key: string, url: string, body: object, fallback: string,
    optimistic?: ListPatch, immediate = false,
  ): Promise<{ ok: boolean; status: number; message: string; data: RestockV2ListResponse }> => {
    if (optimistic) setPatch((old) => mergeListPatch(old, optimistic));
    mark(key, true);
    try {
      const response = await api.post<RestockV2ListResponse>(url, body);
      const message = response.data?.message || fallback;
      enqueueSnackbar(message, { variant: 'success' });
      refetch(immediate);
      return { ok: true, status: 200, message, data: response.data || {} };
    } catch (error) {
      if (optimistic) setPatch((old) => dropListPatch(old, optimistic));
      const message = readNokeError(error, '沒有存到，請重試。');
      enqueueSnackbar(message, { variant: 'error' });
      return { ok: false, status: httpStatusOf(error), message, data: {} };
    } finally {
      mark(key, false);
    }
  }, [enqueueSnackbar, mark, refetch]);

  const decide = useCallback(async (
    sku: string, decision: CounterListDecision, tier?: TierKey | null, reason?: string | null,
  ): Promise<boolean> => (await write(
    sku, DECIDE_URL, decideBody(newReference('v2-list-decide'), sku, decision, tier, reason),
    decision === 'approve' ? '已加入清單' : '已排除這款，不會再建議',
    { decided: { [sku]: decision === 'approve' ? 'approved' : 'rejected' }, removed: {} },
  )).ok, [write]);

  const decideAll = useCallback(async (rows: RestockV2ListProposal[]) => {
    if (!rows.length) return;
    const decided: ListPatch['decided'] = {};
    rows.forEach((row) => { decided[row.sku] = 'approved'; });
    await write(BATCH_KEY, BATCH_URL, decideBatchBody(newReference('v2-list-batch'), rows),
      `已把 ${rows.length} 款加入清單`, { decided, removed: {} });
  }, [write]);

  /**
   * 照建議設定主打 — fill in every un-pinned approved style's 主打 in one write.
   *
   * It sends the picks the SCREEN is showing (`top_variants`, the ranking the 熱銷 badge names),
   * so what lands is exactly what he just read. No optimistic patch: 主打 rows are drawn from
   * the plan and the refresh that follows is what makes them appear, which keeps one source of
   * truth for a badge that has to agree with the engine.
   */
  const setMainsFromSuggestion = useCallback(async (picks: MainBatchPick[]) => {
    if (!picks.length) return;
    const styles = new Set(picks.map((pick) => pick.sku)).size;
    await write(MAIN_BATCH_KEY, MAIN_BATCH_URL, mainBatchBody(newReference('v2-list-main-batch'), picks),
      `已照建議設定 ${styles} 款的主打`, { decided: {}, removed: {} });
  }, [write]);

  const add = useCallback(async (sku: string) => {
    // The picker's row has no tier or depth, so there is no 我的清單 row to build here: the SKU is
    // marked decided (the hit reads 已在清單 at once) and the engine supplies the row.
    await write(sku, ADD_URL, listSkuBody(newReference('v2-list-add'), sku), '已加入清單',
      { decided: { [sku]: 'approved' }, removed: {} });
  }, [write]);

  // One verb for two places: 我的清單's 移除 and 已排除's 加回 both DELETE the row, which is what
  // puts the style back to 待決定 so the engine may propose it again.
  const remove = useCallback(async (sku: string) => {
    await write(sku, REMOVE_URL, listSkuBody(newReference('v2-list-remove'), sku), '已從清單移除',
      { decided: {}, removed: { [sku]: true } });
  }, [write]);

  // The one write with no local model: it inserts a row for every SKU on the rail, so there is
  // nothing to patch — it asks for the recompute straight away instead of joining the debounce.
  const seed = useCallback(async () => {
    await write(SEED_KEY, SEED_URL, listSeedBody(newReference('v2-list-seed')),
      '已用現在架上的貨建立清單', undefined, true);
  }, [write]);

  const retire = useCallback((landed: ListPatch) => {
    setPatch((old) => dropListPatch(old, landed));
  }, []);

  const clearSearch = useCallback(() => {
    searchId.current += 1;
    setResults([]);
    setSearched(false);
    setSearchError('');
  }, []);

  /**
   * The one GET behind both paths. Returns the rows so the caller can decide what they mean;
   * `null` means this query was overtaken by a newer one (or failed) and nothing should act on it.
   */
  const runSearch = useCallback(async (q: string): Promise<RestockV2ListSearchRow[] | null> => {
    const id = ++searchId.current;
    setSearching(true);
    setSearchError('');
    try {
      const response = await api.get(SEARCH_URL, { params: { q } });
      if (searchId.current !== id) return null;
      const rows = searchRows(response?.data);
      setResults(rows);
      setSearched(true);
      return rows;
    } catch (error) {
      if (searchId.current !== id) return null;
      setResults([]);
      setSearched(true);
      setSearchError(readNokeError(error, '查不到商品，請重試。'));
      return null;
    } finally {
      if (searchId.current === id) setSearching(false);
    }
  }, []);

  const search = useCallback(async (query: string) => {
    const q = query.trim();
    if (!q) { clearSearch(); return; }
    await runSearch(q);
  }, [clearSearch, runSearch]);

  /**
   * Type a SKU, press Enter, it is on the list.
   *
   * The lookup is NOT an intermediate step he has to click through — it is how the screen learns
   * which of the five honest answers this is, and it is the same endpoint and the same rows the
   * name search uses (Magnus asked for one mechanism, not two). An exact, undecided SKU is added in
   * the same keystroke; the results list is cleared in that case so he gets ONE answer rather than
   * an answer plus a list he now has to ignore.
   *
   * When the picker finds nothing at all we still ASK THE SERVER, because the server is the only
   * place that can tell a 下架 style from a SKU that never existed — the picker only ever sees
   * active products and counter stock, so 「找不到」 derived here would be a guess.
   */
  const addBySku = useCallback(async (query: string): Promise<ManualAdd | null> => {
    const q = query.trim();
    if (!q) return null;
    const rows = await runSearch(q);
    if (rows === null) return null;
    const hit = exactSearchHit(rows, q);

    if (!hit) {
      // A name, or a SKU whose prefix matched other styles: this is the secondary path, and the
      // hits below the box are the answer. Nothing is written.
      if (rows.length) {
        return { kind: 'hits', query: q, sku: '', row: null, title: '', message: ADD_HITS_NOTE(q, rows.length) };
      }
      const blind = await write(ADD_KEY, ADD_URL, listSkuBody(newReference('v2-list-add'), q),
        '已加入清單', { decided: { [q]: 'approved' }, removed: {} });
      if (blind.ok) {
        const title = blind.data.title || q;
        return { kind: 'added', query: q, sku: q, row: null, title, message: blind.message };
      }
      const kind: ManualAddKind = blind.status === 409 ? 'delisted' : blind.status === 404 ? 'missing' : 'error';
      const fallback = kind === 'delisted' ? ADD_DELISTED_FALLBACK(q) : ADD_MISSING_FALLBACK(q);
      return { kind, query: q, sku: q, row: null, title: '', message: blind.message || fallback };
    }

    clearSearch();
    if (hit.on_list === 'approved') {
      return { kind: 'already', query: q, sku: hit.sku, row: hit, title: hit.title, message: ADD_ALREADY_NOTE };
    }
    if (hit.on_list === 'rejected') {
      return { kind: 'rejected', query: q, sku: hit.sku, row: hit, title: hit.title, message: ADD_REJECTED_NOTE };
    }
    const done = await write(hit.sku, ADD_URL, listSkuBody(newReference('v2-list-add'), hit.sku),
      '已加入清單', { decided: { [hit.sku]: 'approved' }, removed: {} });
    return {
      kind: done.ok ? 'added' : 'error',
      query: q, sku: hit.sku, row: hit, title: hit.title,
      message: done.ok ? addedNote(hit) : done.message,
    };
  }, [clearSearch, runSearch, write]);

  return {
    pending, patch, retire, decide, decideAll, setMainsFromSuggestion, add, addBySku, remove, seed, search, clearSearch,
    results, searched, searching, searchError,
  };
}

export default useCounterList;
